import { useEffect, useRef, useState, type RefObject } from "react";
import { Link } from "react-router";
import { observer } from "mobx-react-lite";
import { useTranslation } from "react-i18next";
import { Compass, PanelLeftClose, PanelLeftOpen, Sparkles, Users, History, BadgePlus, Flame } from "lucide-react";
import { useStore } from "../../stores/rootStore";
import type { DiscoverTab } from "../../lib/discoverTabs";
import { topicPath } from "../../lib/topics";
import { Skeleton } from "../ui/Skeleton";
import { useMediaQuery } from "../../hooks/useMediaQuery";
import { DiscoveryDownload } from "./DiscoveryDownload";
import { SiteFooter } from "./SiteFooter";

/** CMS already supplies emoji; the compact rail uses that same symbol. */
function compactLabel(title: string) {
	return [...new Intl.Segmenter(undefined, { granularity: "grapheme" }).segment(title.trim())][0]?.segment ?? "·";
}

export const DiscoverySidebar = observer(function DiscoverySidebar({ isExplore, feedTab, categoryId, topic, navRef, scrollRef }: {
	isExplore: boolean; feedTab: DiscoverTab | null; categoryId: string | null; topic: string | null;
	navRef: RefObject<HTMLElement | null>; scrollRef: RefObject<HTMLDivElement | null>;
}) {
	const { auth, discover, ui } = useStore();
	const { t } = useTranslation();
	const mobile = useMediaQuery("(max-width: 767px)");
	const [hovered, setHovered] = useState(false);
	const [keyboardFocus, setKeyboardFocus] = useState(false);
	const [peekDismissed, setPeekDismissed] = useState(false);
	const leaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
	const clearLeaveTimer = () => { if (leaveTimer.current) clearTimeout(leaveTimer.current); };
	useEffect(() => () => clearLeaveTimer(), []);
	// Hover is temporary: it must never change the saved width or reflow the games.
	const peekOpen = !mobile && ui.discoveryCollapsed && !peekDismissed && (hovered || keyboardFocus);
	const expanded = mobile ? ui.discoveryMobileOpen : !ui.discoveryCollapsed || peekOpen;
	const toggleLabel = t(mobile || !ui.discoveryCollapsed ? "discover.collapseSidebar" : peekOpen ? "discover.pinSidebar" : "discover.expandSidebar");
	const toggleSidebar = () => {
		clearLeaveTimer();
		setHovered(false); setKeyboardFocus(false); setPeekDismissed(true);
		if (mobile) ui.setDiscoveryMobileOpen(false);
		else ui.toggleDiscoverySidebar();
	};
	useEffect(() => { if (!mobile) ui.setDiscoveryMobileOpen(false); }, [mobile, ui]);
	useEffect(() => {
		const element = scrollRef.current;
		if (mobile || !element) return;
		let idleTimer: ReturnType<typeof setTimeout>;
		const onScroll = () => {
			element.dataset.scrolling = "true";
			clearTimeout(idleTimer);
			idleTimer = setTimeout(() => { delete element.dataset.scrolling; }, 800);
		};
		element.addEventListener("scroll", onScroll, { passive: true });
		return () => {
			clearTimeout(idleTimer);
			element.removeEventListener("scroll", onScroll);
			delete element.dataset.scrolling;
		};
	}, [mobile, scrollRef]);
	const entries = [
		{ href: "/", label: t("discover.explore"), icon: Compass, active: isExplore },
		{ href: "/?tab=for-you", label: t("discover.forYou"), icon: Sparkles, active: feedTab === "forYou" },
		...(auth.isLoggedIn && !auth.isGuest ? [{ href: "/?tab=recently-played", label: t("discover.recentlyPlayed"), icon: History, active: feedTab === "recentlyPlayed" }] : []),
		{ href: "/?tab=latest", label: t("discover.latest"), icon: BadgePlus, active: feedTab === "latest" },
		{ href: "/?tab=popular", label: t("discover.popular"), icon: Flame, active: feedTab === "popular" },
		...(auth.isLoggedIn && !auth.isGuest ? [{ href: "/?tab=following", label: t("discover.following"), icon: Users, active: feedTab === "following" }] : []),
	];
	useEffect(() => {
		if (!mobile || !ui.discoveryMobileOpen) return;
		const nav = navRef.current;
		const previous = document.activeElement as HTMLElement | null;
		const links = () => Array.from(nav?.closest("aside")?.querySelectorAll<HTMLElement>("a[href],button:not(:disabled)") ?? []).filter((node) => node.getClientRects().length > 0);
		links()[0]?.focus();
		const escape = (event: KeyboardEvent) => {
			if (event.key === "Escape") ui.setDiscoveryMobileOpen(false);
			if (event.key === "Tab") {
				const items = links();
				if (event.shiftKey && document.activeElement === items[0]) { event.preventDefault(); items.at(-1)?.focus(); }
				else if (!event.shiftKey && document.activeElement === items.at(-1)) { event.preventDefault(); items[0]?.focus(); }
			}
		};
		document.addEventListener("keydown", escape);
		return () => {
			document.removeEventListener("keydown", escape);
			if (previous?.isConnected && previous.getClientRects().length) previous.focus();
		};
	}, [mobile, ui.discoveryMobileOpen, ui, navRef]);
	return <>
		{ui.discoveryMobileOpen ? <button className="discovery-nav-backdrop" aria-label={t("discover.collapseSidebar")} onClick={() => ui.setDiscoveryMobileOpen(false)} /> : null}
		<aside id="discovery-sidebar" className="discover-navigation" data-peek-open={peekOpen} aria-label={t("discover.categories")}
			onPointerEnter={(event) => {
				if (event.pointerType === "touch") return;
				clearLeaveTimer(); setHovered(true); setPeekDismissed(false);
			}}
			onPointerLeave={() => {
				clearLeaveTimer();
				leaveTimer.current = setTimeout(() => { setHovered(false); setPeekDismissed(false); }, 160);
			}}
			onFocusCapture={(event) => {
				if (event.target.matches(":focus-visible")) { setKeyboardFocus(true); setPeekDismissed(false); }
			}}
			onBlurCapture={(event) => { if (!event.currentTarget.contains(event.relatedTarget)) setKeyboardFocus(false); }}
			onKeyDown={(event) => {
				if (event.key === "Escape" && peekOpen) { setPeekDismissed(true); event.stopPropagation(); }
			}}>
			<div className="discover-navigation-inner">
				<div ref={scrollRef} className="discover-sidebar-scroll">
					<nav ref={navRef} aria-label={t("discover.categories")} className="discover-nav" onClick={(event) => {
						if ((event.target as Element).closest("a")) ui.setDiscoveryMobileOpen(false);
					}}>
						{entries.map(({ href, label, icon: Icon, active }) => <Link key={href} to={href} aria-current={active ? "page" : undefined}
							aria-label={label} title={label} className="discover-nav-link"><Icon size={17} /><span className="discover-nav-label">{label}</span></Link>)}
						{discover.tagSections.length > 0 || !discover.tagSectionsSettled || topic ? <div role="separator" className="discover-nav-divider" /> : null}
						{discover.tagSections.map((section) => <Link key={section.id} to={"/?category=" + encodeURIComponent(section.id)}
							aria-current={categoryId === section.id ? "page" : undefined} aria-label={section.title!} title={section.title!} className="discover-nav-link">
							<span className="discover-nav-symbol" aria-hidden="true">{compactLabel(section.title!)}</span><span className="discover-nav-label">{section.title}</span>
						</Link>)}
						{!discover.tagSectionsSettled ? <div role="status" aria-label={t("common.loading")} className="grid gap-5 px-3">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-4 w-full rounded" />)}</div> : null}
						{topic ? <Link to={topicPath(topic)} aria-current="page" aria-label={"#" + topic} title={"#" + topic} className="discover-nav-link"><span className="discover-nav-symbol" aria-hidden="true">#</span><span className="discover-nav-label">#{topic}</span></Link> : null}
					</nav>
					<DiscoveryDownload />
					<SiteFooter variant="sidebar" />
				</div>
				<button type="button" onClick={toggleSidebar} className="discovery-sidebar-toggle" aria-controls="discovery-sidebar"
					aria-expanded={expanded} aria-label={toggleLabel} title={toggleLabel}>
					{mobile || !ui.discoveryCollapsed ? <PanelLeftClose size={18} /> : <PanelLeftOpen size={18} />}<span>{toggleLabel}</span>
				</button>
			</div>
		</aside>
	</>;
});
